import { connectorsForWallets } from '@rainbow-me/rainbowkit';
import {
  binanceWallet,
  bitgetWallet,
  imTokenWallet,
  metaMaskWallet,
  okxWallet,
  rabbyWallet,
  tokenPocketWallet,
  trustWallet,
  walletConnectWallet,
} from '@rainbow-me/rainbowkit/wallets';
import { isMobile } from 'react-device-detect';
import { http } from 'viem';
import { createConfig } from 'wagmi';

import config from 'configs/app';
import currentChain from 'lib/web3/currentChain';

const feature = config.features.blockchainInteraction;

const appName = `${ config.chain.name } explorer`;

// Display-name overrides to match the product spec.
const trustWalletExtension = (...args: Parameters<typeof trustWallet>): ReturnType<typeof trustWallet> => ({
  ...trustWallet(...args),
  name: 'Trust Wallet Extension',
});

const walletConnect = (...args: Parameters<typeof walletConnectWallet>): ReturnType<typeof walletConnectWallet> => ({
  ...walletConnectWallet(...args),
  name: 'Wallet Connect',
});

const wagmiConfig = (() => {
  if (!feature.isEnabled) {
    return createConfig({
      chains: [ currentChain ],
      transports: {
        [currentChain.id]: http(config.chain.rpcUrl || `${ config.api.endpoint }/api/eth-rpc`),
      },
      ssr: true,
      batch: { multicall: { wait: 100 } },
    });
  }

  // Desktop and mobile show different curated wallets. `isMobile` is read from the UA at
  // module load — on the client (where the connect modal renders) it reflects the real
  // device; on the server it defaults to desktop, which is fine since the modal is
  // client-only.
  const wallets = isMobile ?
    [
      metaMaskWallet,
      rabbyWallet,
      okxWallet,
      bitgetWallet,
      binanceWallet,
      imTokenWallet,
      tokenPocketWallet,
      walletConnect,
    ] :
    [
      metaMaskWallet,
      rabbyWallet,
      okxWallet,
      trustWalletExtension,
      binanceWallet,
      walletConnect,
    ];

  const connectors = connectorsForWallets(
    [
      {
        groupName: 'Wallets',
        wallets,
      },
    ],
    {
      appName,
      projectId: feature.walletConnect.projectId,
    },
  );

  return createConfig({
    chains: [ currentChain ],
    connectors,
    transports: {
      [currentChain.id]: http(),
    },
    multiInjectedProviderDiscovery: true,
    // Match the working `web` project (ssr:false). With ssr:true wagmi runs an eager
    // hydration reconnect that probes every connector's isAuthorized(); injected
    // connectors that fall back to a shared window.ethereum (e.g. OKX) falsely pass and
    // get attached as extra connections — and an authorized wallet even resurrects itself
    // right after the user disconnects. ssr:false avoids that eager probe. The remaining
    // phantom connections during a wallet switch are handled by useEnforceSingleWallet and
    // the debounced account in useWallet.
    ssr: false,
    batch: { multicall: { wait: 100 } },
  });
})();

export default wagmiConfig;
